import React, { useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  ReactFlowProvider,
  Handle,
  Position,
  MarkerType,
  NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Bot,
  Play,
  MessageCircle,
  GitBranch,
  Sparkles,
  ArrowDownCircle,
  ArrowUpCircle,
} from 'lucide-react';
import { SharedWorkflowStepDetail } from '../../types';
import { useThemeStore } from '../../store/themeStore';

/** Props for the SharedFlowVisualization component */
interface SharedFlowVisualizationProps {
  steps: SharedWorkflowStepDetail[];
  className?: string;
}

/** Data shape for each shared flow node */
interface SharedNodeData {
  label: string;
  stepType: string;
  description: string | null;
}

/** Step type configuration for consistent styling */
const STEP_TYPE_CONFIG: Record<
  string,
  { label: string; color: string; darkColor: string; icon: React.ReactNode }
> = {
  run_agent: {
    label: 'Agent',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    darkColor: 'dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
    icon: <Bot className="w-3.5 h-3.5" />,
  },
  user_prompt: {
    label: 'User Prompt',
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    darkColor: 'dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
    icon: <MessageCircle className="w-3.5 h-3.5" />,
  },
  auto_prompt: {
    label: 'Auto Prompt',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    darkColor: 'dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700',
    icon: <Sparkles className="w-3.5 h-3.5" />,
  },
  conditional: {
    label: 'Conditional',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    darkColor: 'dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
    icon: <GitBranch className="w-3.5 h-3.5" />,
  },
  queue_write: {
    label: 'Queue Write',
    color: 'bg-teal-100 text-teal-700 border-teal-300',
    darkColor: 'dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-700',
    icon: <ArrowUpCircle className="w-3.5 h-3.5" />,
  },
  queue_read: {
    label: 'Queue Read',
    color: 'bg-cyan-100 text-cyan-700 border-cyan-300',
    darkColor: 'dark:bg-cyan-900/40 dark:text-cyan-300 dark:border-cyan-700',
    icon: <ArrowDownCircle className="w-3.5 h-3.5" />,
  },
};

const DEFAULT_STEP_CONFIG = {
  label: 'Step',
  color: 'bg-gray-100 text-gray-700 border-gray-300',
  darkColor: 'dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600',
  icon: <Play className="w-3.5 h-3.5" />,
};

/** Custom read-only node for the shared flow diagram */
const SharedFlowNode: React.FC<NodeProps<SharedNodeData>> = ({ data }) => {
  const config = STEP_TYPE_CONFIG[data.stepType] || DEFAULT_STEP_CONFIG;

  return (
    <div className="w-56 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm">
      {/* Target handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-gray-400 dark:!bg-gray-500 !border-0"
        isConnectable={false}
      />

      {/* Step type badge */}
      <div className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-1.5">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${config.color} ${config.darkColor}`}
        >
          {config.icon}
          {config.label}
        </span>
      </div>

      {/* Node content */}
      <div className="px-3 py-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {data.label}
        </p>
        {data.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
            {data.description}
          </p>
        )}
      </div>

      {/* Source handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !bg-gray-400 dark:!bg-gray-500 !border-0"
        isConnectable={false}
      />
    </div>
  );
};

/** Node types registry for ReactFlow */
const nodeTypes = {
  sharedStep: SharedFlowNode,
};

/** Vertical spacing between nodes in auto-layout */
const VERTICAL_SPACING = 120;
/** Horizontal center for auto-layout */
const CENTER_X = 200;
/** Node width for edge routing calculations */
const NODE_WIDTH = 224; // w-56 = 14rem = 224px

/**
 * SharedFlowVisualization renders workflow steps as a read-only
 * React Flow diagram. It converts SharedWorkflowStepDetail[] into
 * nodes and edges, using stored positions when available or falling
 * back to a simple vertical auto-layout.
 */
const SharedFlowVisualizationInner: React.FC<SharedFlowVisualizationProps> = ({
  steps,
  className = '',
}) => {
  const isDark = useThemeStore((s) => s.resolvedTheme) === 'dark';

  const { nodes, edges } = useMemo(() => {
    if (steps.length === 0) return { nodes: [], edges: [] };

    // Determine if steps have stored positions
    const hasPositions = steps.some(
      (s) => typeof s.position_x === 'number' && typeof s.position_y === 'number'
    );

    // Sort steps by sequence: use previous_step_id chain to determine order
    // Steps without previous_step_id are roots
    const stepMap = new Map<number, SharedWorkflowStepDetail>();
    steps.forEach((s) => stepMap.set(s.orchestration_step_id, s));

    // Build ordered list for auto-layout fallback
    const orderedSteps: SharedWorkflowStepDetail[] = [];
    const placed = new Set<number>();

    // Find root steps (no previous_step_id or previous_step_id not in this set)
    const stepIds = new Set(steps.map((s) => s.orchestration_step_id));
    const roots = steps.filter(
      (s) => s.previous_step_id === null || !stepIds.has(s.previous_step_id)
    );

    // BFS from roots to get order
    const queue = [...roots];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (placed.has(current.orchestration_step_id)) continue;
      placed.add(current.orchestration_step_id);
      orderedSteps.push(current);

      // Find steps that follow this one
      const children = steps.filter(
        (s) =>
          s.previous_step_id === current.orchestration_step_id &&
          !placed.has(s.orchestration_step_id)
      );
      queue.push(...children);
    }

    // Add any remaining unconnected steps
    steps.forEach((s) => {
      if (!placed.has(s.orchestration_step_id)) {
        orderedSteps.push(s);
      }
    });

    // Create nodes
    const flowNodes: Node<SharedNodeData>[] = orderedSteps.map((step, index) => ({
      id: String(step.orchestration_step_id),
      type: 'sharedStep',
      position: hasPositions && typeof step.position_x === 'number' && typeof step.position_y === 'number'
        ? { x: step.position_x, y: step.position_y }
        : { x: CENTER_X, y: index * VERTICAL_SPACING },
      data: {
        label: step.label || step.description || step.step_type || 'Step',
        stepType: step.step_type || 'step',
        description: step.description,
      },
      draggable: false,
      connectable: false,
      selectable: false,
    }));

    // Create edges from previous_step_id relationships
    const flowEdges: Edge[] = steps
      .filter((step) => step.previous_step_id !== null && stepIds.has(step.previous_step_id!))
      .map((step) => {
        // Determine handle positions based on node positions
        let sourceHandle = 'bottom';
        let targetHandle = 'top';

        if (hasPositions) {
          const sourceStep = stepMap.get(step.previous_step_id!);
          if (sourceStep && typeof sourceStep.position_x === 'number' && typeof step.position_x === 'number') {
            const dx = step.position_x - sourceStep.position_x;
            const dy = (step.position_y ?? 0) - (sourceStep.position_y ?? 0);

            if (Math.abs(dx) > NODE_WIDTH && dx > 0) {
              sourceHandle = 'right';
              targetHandle = 'left';
            } else if (Math.abs(dx) > NODE_WIDTH && dx < 0) {
              sourceHandle = 'left';
              targetHandle = 'right';
            } else if (dy < 0) {
              sourceHandle = 'top';
              targetHandle = 'bottom';
            }
          }
        }

        return {
          id: `e-${step.previous_step_id}-${step.orchestration_step_id}`,
          source: String(step.previous_step_id),
          target: String(step.orchestration_step_id),
          sourceHandle,
          targetHandle,
          style: {
            stroke: isDark ? '#6b7280' : '#9ca3af',
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: isDark ? '#6b7280' : '#9ca3af',
          },
        };
      });

    return { nodes: flowNodes, edges: flowEdges };
  }, [steps, isDark]);

  if (nodes.length === 0) return null;

  return (
    <div className={`${className}`} data-testid="shared-flow-visualization">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1.2 }}
        attributionPosition="bottom-right"
        className="bg-gray-50 dark:bg-gray-900 rounded-lg"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: false }}
      >
        <Controls showInteractive={false} />
        <Background color={isDark ? '#374151' : '#e5e7eb'} gap={20} size={1} />
      </ReactFlow>
    </div>
  );
};

/** Wrapper with ReactFlowProvider to enable internal hooks */
const SharedFlowVisualization: React.FC<SharedFlowVisualizationProps> = (props) => {
  if (!props.steps || props.steps.length === 0) return null;

  return (
    <ReactFlowProvider>
      <SharedFlowVisualizationInner {...props} />
    </ReactFlowProvider>
  );
};

export default SharedFlowVisualization;
